// src/components/ui/modal/CardModal.tsx
'use client';

import type { ReactNode, CSSProperties } from 'react';
import clsx from 'clsx';
import * as Modal from '@/components/ui/modal/Modal';
import style from './CardModal.module.scss';

export type CardModalProps = {
  open: boolean;
  onClose: () => void;

  /** a11y 標題供 aria-labelledby 使用（畫面不顯示） */
  ariaTitle: string;

  /** ── Slots（全部可選） ───────────────────────────────────────────── */
  /** 頂部封面區（若提供，將覆蓋下方所有封面組態） */
  cover?: ReactNode;
  /** 中間媒體區（動畫 / 圖示） */
  media?: ReactNode;
  /** 文字內容（可自行組 subtitle/title/desc） */
  content?: ReactNode;
  /** 按鈕群組（0~2 顆或更多） */
  actions?: ReactNode;

  /** ── 外觀 ───────────────────────────────────────────────────────── */
  dialogSize?: 'sm' | 'lg' | 'xl';
  className?: string; // 加在 modal-content
  closeOnBackdrop?: boolean; // 預設 true
  lockScroll?: boolean; // 預設 true
  showCloseButton?: boolean; // 預設 true

  /** 對應骨架節點的客製 className */
  classNames?: {
    root?: string; // 套在最外層 .card
    cover?: string; // 套在 cover 容器（或預設封面）
    media?: string; // 套在 media 容器
    content?: string; // 套在 content 容器
    actions?: string; // 套在 actions 容器
  };

  /** ── 封面：直接指定背景（簡單用） ───────────────────────────────── */
  /** 內建三款背景：'1' | '2' | '3'（若 bgImage 有值則忽略） */
  bgVariant?: '1' | '2' | '3';
  /** 自訂背景圖（優先於 bgVariant） */
  bgImage?: string;

  /** ── 封面：預設集合（進階；用於「不同狀態不同封面」） ─────────── */
  /**
   * 當 cover 未提供時，會依照 coverPresetKey 從 coverPresets 取對應的
   * { bgVariant?, bgImage? }。若找到 bgImage 則採用；否則採用 bgVariant；
   * 若兩者皆無，回退到上方的 bgImage/bgVariant；若仍無，則不顯示封面。
   */
  coverPresetKey?: string;
  coverPresets?: Record<string, { bgVariant?: '1' | '2' | '3'; bgImage?: string }>;
};

const CardModal: React.FC<CardModalProps> = ({
  open,
  onClose,
  ariaTitle,

  // slots
  cover,
  media,
  content,
  actions,

  // appearance
  dialogSize = 'sm',
  className,
  closeOnBackdrop = true,
  lockScroll = true,
  showCloseButton = true,
  classNames,

  // direct cover control
  bgVariant,
  bgImage,

  // preset-based cover
  coverPresetKey,
  coverPresets,
}) => {
  /** 封面建構（當外部未提供 cover slot 時才會啟用） */
  const resolvedCover: ReactNode | null = (() => {
    if (cover) return cover;

    // 1) 若有 presetKey，先取對應 preset
    const preset = coverPresetKey && coverPresets ? coverPresets[coverPresetKey] : undefined;

    // 2) 依優先序決定最終的 bgImage / bgVariant
    const finalBgImage = preset?.bgImage ?? bgImage;
    const finalBgVariant = preset?.bgVariant ?? bgVariant;

    // 3) 兩者皆無 → 不顯示封面
    if (!finalBgImage && !finalBgVariant) return null;

    // 4) 準備 class 與 style
    const topBgClass =
      !finalBgImage &&
      clsx({
        [style.bgCard1]: finalBgVariant === '1',
        [style.bgCard2]: finalBgVariant === '2',
        [style.bgCard3]: finalBgVariant === '3',
      });

    const topBgStyle: CSSProperties | undefined = finalBgImage
      ? {
          backgroundImage: `url(${finalBgImage})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }
      : undefined;

    return (
      <div
        className={clsx('card-img-top', style.cardTop, topBgClass, classNames?.cover)}
        style={topBgStyle}
      >
        <span />
      </div>
    );
  })();

  return (
    <Modal.Root
      open={open}
      onClose={onClose}
      closeOnBackdrop={closeOnBackdrop}
      lockScroll={lockScroll}
      className="modal-card"
    >
      <Modal.Dialog size={dialogSize} centered>
        <Modal.Content className={clsx('border-0 shadow-lg overflow-hidden', className)}>
          {/* a11y 標題 */}
          <Modal.Title className="visually-hidden">{ariaTitle}</Modal.Title>

          {/* 右上角關閉鈕 */}
          {showCloseButton && (
            <div className="position-absolute end-0 top-0 p-2 z-50">
              <button type="button" className="btn-close" aria-label="Close" onClick={onClose} />
            </div>
          )}

          {/* 卡片骨架（沿用原結構 class，便於接軌既有樣式） */}
          <div className={clsx('card profile-card-with-cover mb-0 border-0', classNames?.root)}>
            {/* Cover（slot 優先；否則用上方 resolvedCover） */}
            {resolvedCover}

            {/* Media Slot */}
            {media && (
              <div className={clsx('card-profile-image', classNames?.media)}>
                <div className="media text-center">
                  <div className="media-body">{media}</div>
                </div>
              </div>
            )}

            {/* Content Slot */}
            {content && (
              <div className={clsx('profile-card-content', classNames?.content)}>
                <div className="card-body text-center">{content}</div>
              </div>
            )}

            {/* Actions Slot */}
            {actions && <div className={clsx('px-4 pb-4', classNames?.actions)}>{actions}</div>}
          </div>
        </Modal.Content>
      </Modal.Dialog>
    </Modal.Root>
  );
};

export default CardModal;
